import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiAward, FiUsers, FiTruck, FiShield, FiHeart, FiClock } from 'react-icons/fi';
import Breadcrumb from '../components/common/Breadcrumb';
import api from '../services/api';

const AboutPage = () => {
  const [continueShoppingLink, setContinueShoppingLink] = useState('/category/new-in');

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.continueShoppingLink) {
          setContinueShoppingLink(res.continueShoppingLink);
        }
      } catch (err) {
        console.error('Failed to fetch continue shopping link', err);
      }
    };
    fetchLink();
  }, []);

  const stats = [
    { label: 'Years of Excellence', value: '10+', icon: FiClock },
    { label: 'Happy Customers', value: '50K+', icon: FiUsers },
    { label: 'Premium Brands', value: '50+', icon: FiAward },
    { label: 'Orders Delivered', value: '100K+', icon: FiTruck }
  ];

  const values = [
    { icon: FiShield, title: '100% Authentic', description: 'Every watch we sell is guaranteed authentic with complete documentation.' },
    { icon: FiHeart, title: 'Customer First', description: 'Our customers are at the heart of everything we do.' },
    { icon: FiTruck, title: 'Fast Delivery', description: 'Quick and reliable shipping across Pakistan.' }
  ];

  const team = [
    { name: 'Ali Raza', position: 'Founder & CEO', image: 'https://randomuser.me/api/portraits/men/1.jpg', bio: 'Watch enthusiast with 15+ years of industry experience.' },
    { name: 'Fatima Khan', position: 'Head of Operations', image: 'https://randomuser.me/api/portraits/women/2.jpg', bio: 'Ensuring smooth operations and customer satisfaction.' },
    { name: 'Usman Malik', position: 'Watch Specialist', image: 'https://randomuser.me/api/portraits/men/3.jpg', bio: 'Expert in luxury timepieces and authentication.' }
  ];

  return (
    <div className="bg-white min-h-screen">
      <div className="border-b">
        <div className="container py-4">
          <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'About Us', path: '/about' }]} />
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Stylo Watches</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">Pakistan's premier destination for authentic luxury and smart watches. We bring you the finest timepieces from around the world.</p>
        </div>
      </section>
      <section className="py-16">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600">
                <p>Founded in 2015, Stylo Watches began with a simple mission: to provide watch enthusiasts in Pakistan with access to authentic, high-quality timepieces at competitive prices.</p>
                <p>What started as a small passion project has grown into one of the country's most trusted watch retailers. We've partnered with authorized distributors of the world's finest watch brands to ensure every piece we sell is 100% genuine.</p>
                <p>Today, we're proud to serve thousands of satisfied customers across Pakistan, helping them find the perfect watch for every occasion.</p>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1547996160-81dfa63595aa?w=600" alt="Our Story" className="rounded-lg shadow-xl" />
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">2015</p>
                <p className="text-sm opacity-90">Year Founded</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Meet Our Team</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <img src={member.image} alt={member.name} className="w-full h-64 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 bg-blue-600">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Find Your Perfect Watch?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">Explore our collection of premium watches from world-renowned brands</p>
          <Link to={continueShoppingLink} className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors">Shop Now</Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;